import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import Table from 'atoms/Table';
import SmallLoadingIndicator from 'atoms/SmallLoadingIndicator';
import ValidateSentModal from 'components/ValidateSentModal';
import { headerStyle } from 'containers/App/constants';
import { makeSelectIsMobile } from 'containers/App/selectors';

import { fetchPatientRois, revokePatientRoi, renewPatientRoi } from './actions';
import {
	makeSelectPatientRois,
	makeSelectPatientRoisLoading,
	makeSelectPatientRoisRevoking,
} from './selectors';

const statusBadge = (status) => {
	const colors = {
		matched: 'badge-success',
		awaiting_signature: 'badge-info',
		active: 'badge-warning',
		pending_review: 'badge-warning',
		revoked: 'badge-danger',
		expired: 'badge-secondary',
	};
	const cls = colors[status] || 'badge-secondary';
	const label = (status || '').replace(/_/g, ' ');
	return (
		<span className={`badge ${cls} font-10 px-2 py-1 text-uppercase`}>
			{label}
		</span>
	);
};

class PatientRois extends Component {
	state = {
		showRevokeModal: false,
		activeRoiId: null,
	};

	componentDidMount() {
		this.props.fetchRois();
	}

	handleRevokeClick = (roiId) => {
		this.setState({ showRevokeModal: true, activeRoiId: roiId });
	};

	handleConfirmRevoke = () => {
		this.props.revokeRoi(this.state.activeRoiId);
	};

	componentDidUpdate(prevProps) {
		if (prevProps.revoking && !this.props.revoking && this.state.showRevokeModal) {
			this.setState({ showRevokeModal: false, activeRoiId: null });
		}
	}

	getColumns = () => [
		{
			field: 'clinicianName',
			title: 'RECIPIENT',
			headerStyle,
			render: (row) => (
				<div className="mt-2 mx-2 font-13">
					{row.clinicianName || '—'}
				</div>
			),
		},
		{
			field: 'createdAt',
			title: 'SUBMITTED ON',
			headerStyle,
			render: (row) => (
				<div className="mt-2 mx-2 font-13">
					{moment(row.createdAt).format('YYYY-MM-DD')}
				</div>
			),
		},
		{
			field: 'status',
			title: 'STATUS',
			headerStyle,
			render: (row) => (
				<div className="mt-2 mx-2">{statusBadge(row.status)}</div>
			),
		},
		{
			field: 'expiresAt',
			title: 'EXPIRES',
			headerStyle,
			render: (row) => (
				<div className="mt-2 mx-2 font-13">
					{row.expiresAt ? moment(row.expiresAt).format('YYYY-MM-DD') : '—'}
				</div>
			),
		},
		{
			field: 'document',
			title: 'DOCUMENT',
			headerStyle,
			render: (row) => {
				const uuid = row.userDocumentId?.uuid;
				if (!uuid) return <div className="mt-2 mx-2 font-13 text-muted">—</div>;
				return (
					<div
						className="mt-2 mx-2 font-13 hand link-color"
						onClick={() => this.props.viewPdf?.({ uuid, documentType: 'ROI' })}
					>
						VIEW
					</div>
				);
			},
		},
		{
			field: 'actions',
			title: '',
			headerStyle,
			render: (row) => {
				const canRevoke =
					row.status === 'matched' ||
					row.status === 'active' ||
					row.status === 'pending_review';
				const canRenew =
					row.status === 'expired' ||
					(row.status === 'matched' &&
						row.expiresAt &&
						moment(row.expiresAt).diff(moment(), 'days') <= 30);
				return (
					<div className="d-flex">
						{canRenew && (
							<button
								className="btn btn-sm btn-outline-primary font-10 text-uppercase mr-1"
								onClick={() => this.props.renewRoi(row.id)}
							>
								Renew
							</button>
						)}
						{canRevoke && (
							<button
								className="btn btn-sm btn-outline-danger font-10 text-uppercase"
								onClick={() => this.handleRevokeClick(row.id)}
							>
								Revoke
							</button>
						)}
					</div>
				);
			},
		},
	];

	renderMobile = (rois) =>
		rois.map((roi) => (
			<div key={roi.id} className="card mb-2 p-3">
				<div className="d-flex justify-content-between align-items-center mb-2">
					<span className="font-13 font-weight-bold">
						{roi.clinicianName || 'Unknown'}
					</span>
					{statusBadge(roi.status)}
				</div>
				<div className="font-11 text-muted mb-1">
					Submitted: {moment(roi.createdAt).format('YYYY-MM-DD')}
				</div>
				{roi.expiresAt && (
					<div className="font-11 text-muted mb-1">
						Expires: {moment(roi.expiresAt).format('YYYY-MM-DD')}
					</div>
				)}
				{roi.userDocumentId?.uuid && (
					<div
						className="font-11 hand link-color mb-1"
						onClick={() =>
							this.props.viewPdf?.({
								uuid: roi.userDocumentId.uuid,
								documentType: 'ROI',
							})
						}
					>
						VIEW DOCUMENT
					</div>
				)}
				<div className="d-flex mt-2">
					{(roi.status === 'expired' ||
						(roi.status === 'matched' &&
							roi.expiresAt &&
							moment(roi.expiresAt).diff(moment(), 'days') <= 30)) && (
						<button
							className="btn btn-sm btn-outline-primary font-10 text-uppercase mr-2"
							onClick={() => this.props.renewRoi(roi.id)}
						>
							Renew
						</button>
					)}
					{(roi.status === 'matched' ||
						roi.status === 'active' ||
						roi.status === 'pending_review') && (
						<button
							className="btn btn-sm btn-outline-danger font-10 text-uppercase"
							onClick={() => this.handleRevokeClick(roi.id)}
						>
							Revoke
						</button>
					)}
				</div>
			</div>
		));

	render() {
		const { rois, loading, isMobile } = this.props;
		const { showRevokeModal } = this.state;

		return (
			<div>
				{isMobile ? (
					<div className="row mx-1">
						{loading ? (
							<SmallLoadingIndicator
								text={'Loading...'}
								color="var(--main-color)"
							/>
						) : (
							this.renderMobile(rois)
						)}
					</div>
				) : (
					<Table
						keyField="patientRois"
						data={rois}
						columns={this.getColumns()}
						loading={loading}
						placeholder="No ROI records found"
						classes="table-responsive-xl"
					/>
				)}
				{showRevokeModal && (
					<ValidateSentModal
						hide={() =>
							this.setState({ showRevokeModal: false, activeRoiId: null })
						}
						handleDelete={this.handleConfirmRevoke}
						message="You are revoking this ROI. This action is permanent and cannot be undone."
						toBeDeleted={{ name: 'REVOKE' }}
					/>
				)}
			</div>
		);
	}
}

const mapStateToProps = createStructuredSelector({
	rois: makeSelectPatientRois(),
	loading: makeSelectPatientRoisLoading(),
	revoking: makeSelectPatientRoisRevoking(),
	isMobile: makeSelectIsMobile(),
});

function mapDispatchToProps(dispatch) {
	return {
		fetchRois: () => dispatch(fetchPatientRois()),
		revokeRoi: (roiId) => dispatch(revokePatientRoi(roiId)),
		renewRoi: (roiId) => dispatch(renewPatientRoi(roiId)),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(PatientRois);
